import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 색상 토큰 ────────────────────────────────────────────────
const BLUE    = '#3a8fd4';
const BLUE_BG = '#e6f1fb';

const getUserId = () => {
  try {
    const localUser = JSON.parse(localStorage.getItem('currentUser'));
    if (localUser?.userId) return String(localUser.userId);
    if (localUser?.id)     return String(localUser.id);
    if (localUser?.email)  return localUser.email;
  } catch (e) {}
  return 'guest';
};

function TeamManagementPage() {
  const navigate        = useNavigate();
  const location        = useLocation();
  const fileInputRef    = useRef(null);
  const chatMessagesRef = useRef(null);
  const isMobile        = useIsMobile();

  const storedUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const userName   = storedUser.name   || '사용자';
  const userId     = storedUser.userId || storedUser.id || storedUser.email || 'guest';

  const getInitialTeam = () => {
    const uid      = getUserId();
    const allTeams = JSON.parse(localStorage.getItem(`allTeams_${uid}`) || '[]');
    const teamFromState = location.state?.team;
    if (teamFromState) {
      const existingTeam = allTeams.find(t => String(t.id) === String(teamFromState.id));
      return {
        ...teamFromState,
        noticeTitle:   existingTeam?.noticeTitle   || '',
        notice:        existingTeam?.notice        || '',
        noticeHistory: existingTeam?.noticeHistory || [],
        members: (existingTeam?.members || teamFromState.members || []).map(m => ({
          ...m,
          isLeader: m.isLeader ?? m.role?.includes('팀장') ?? false,
          role: m.role?.replace('/팀장', '') || '미정',
        })),
        chatHistory:   existingTeam?.chatHistory   || teamFromState.chatHistory || [],
      };
    }
    const targetId  = localStorage.getItem('lastSavedTeamId') || 1;
    const foundTeam = allTeams.find(t => String(t.id) === String(targetId));
    return foundTeam || { id: targetId, members: [], meetingMinutes: [], chatHistory: [], noticeTitle: '', notice: '', noticeHistory: [] };
  };

  const initialTeam = getInitialTeam();
  const amILeader  = initialTeam.members?.some(m => m.name === userName && m.isLeader) || false;

  const [teamInfo,         setTeamInfo]         = useState({ ...initialTeam });
  const [logs, setLogs] = useState(() => {
    try {
      const saved = localStorage.getItem(`teamLogs_${initialTeam.id}`);
      return saved ? JSON.parse(saved) : [`${userName} 님이 프로젝트를 시작했습니다.`];
    } catch (e) {
      return [`${userName} 님이 프로젝트를 시작했습니다.`];
    }
  });
  const [members, setMembers] = useState(initialTeam.members?.map((m, idx) => ({
    id: m.id || idx, name: m.name || userName,
    role: m.role?.replace('/팀장', '') || '미정',
    isLeader: m.isLeader === true || m.isLeader === 'true' || m.role?.includes('팀장') || false,
    currentTask: m.task || m.currentTask || '',
    todo: m.todo || [], completed: m.completed || [],
    contribution: m.contribution || 0,
  })) || []);
  const [chatMessagesList, setChatMessagesList] = useState(initialTeam.chatHistory || []);
  const [doneContextMenu,  setDoneContextMenu]  = useState({ show: false, x: 0, y: 0, index: null });
  const [customModal,      setCustomModal]      = useState({ show: false, title: '', onConfirm: null, isConfirm: false });
  const [chatInput,        setChatInput]        = useState('');
  const [editModal,        setEditModal]        = useState({ show: false, member: null });
  const [progressModal,    setProgressModal]    = useState(false);
  const [noticeModal,      setNoticeModal]      = useState({ show: false, mode: 'list', selectedNotice: null });
  const [statusMenu,       setStatusMenu]       = useState({ show: false, x: 0, y: 0 });
  // 모바일: 채팅창 토글
  const [showChat,         setShowChat]         = useState(false);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

    // 팀 정보 불러오기 (deadline, notice, status, chatHistory 포함)
    fetch(`${apiUrl}/api/teams/${userId}`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            const cur = data.find(t => String(t.id) === String(teamInfo.id));
            if (cur) {
              setTeamInfo(prev => ({
                ...prev,
                name: cur.name,
                projectTitle: cur.name,
                deadline: cur.deadline && cur.deadline !== '2026-12-31'
                    ? cur.deadline
                    : prev.deadline || '2026-12-31',
                status: cur.status || prev.status || '준비',
                notice: cur.notice || prev.notice || '',
                noticeTitle: cur.noticeTitle || prev.noticeTitle || '',
                noticeHistory: cur.noticeHistory ? JSON.parse(cur.noticeHistory) : prev.noticeHistory || [],
              }));
              if (cur.chatHistory) {
                try {
                  const parsed = JSON.parse(cur.chatHistory);
                  if (Array.isArray(parsed)) {
                    setChatMessagesList(parsed.map(msg => ({
                      ...msg,
                      isMine: String(msg.senderId) === String(userId)
                    })));
                    setTimeout(() => {
                      if (chatMessagesRef.current) chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
                    }, 100);
                  }
                } catch(e) {}
              }
              // ✅ 백엔드에서 활동 로그 불러오기
              if (cur.activityLogs) {
                try {
                  const parsedLogs = JSON.parse(cur.activityLogs);
                  if (Array.isArray(parsedLogs) && parsedLogs.length > 0) {
                    setLogs(parsedLogs);
                    localStorage.setItem(`teamLogs_${teamInfo.id}`, JSON.stringify(parsedLogs));
                  }
                } catch (e) {}
              }
            }
          }
        }).catch(() => {});

    // 백엔드에서 팀원 목록 불러오기
    // 백엔드에서 팀원 목록 불러오기 - 이름 기준으로 merge, localStorage 데이터 우선
    fetch(`${apiUrl}/api/teams/${teamInfo.id}/members`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            setMembers(prev => {
              // ✅ 백엔드 목록 기준으로 merge (todo/completed 등 로컬 데이터 유지)
              return data.map(m => {
                const name = m.user?.name || m.name || '팀원';
                const existing = prev.find(p => p.name === name);
                return existing
                    ? { ...existing, id: m.id } // 로컬 데이터 유지 + 백엔드 id 동기화
                    : {
                      id: m.id,
                      name,
                      role: m.role || '팀원',
                      isLeader: m.role === '팀장',
                      currentTask: '',
                      todo: [],
                      completed: [],
                      contribution: 0,
                    };
              });
            });
          }
        }).catch(() => {});
  }, [userId, teamInfo.id]);

  // 백엔드에 팀 정보 저장 (변경될 때마다)
  // 백엔드에 팀 정보 저장 (변경될 때마다)
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    if (!teamInfo.id) return;
    const timer = setTimeout(() => {
      fetch(`${apiUrl}/api/teams/${teamInfo.id}/info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deadline: teamInfo.deadline,
          status: teamInfo.status,
          notice: teamInfo.notice,
          noticeTitle: teamInfo.noticeTitle,
          noticeHistory: JSON.stringify(teamInfo.noticeHistory || []),
        })
      }).catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [teamInfo.deadline, teamInfo.status, teamInfo.notice, teamInfo.noticeTitle, teamInfo.noticeHistory]);

  useEffect(() => {
    const uid      = getUserId();
    const allTeams = JSON.parse(localStorage.getItem(`allTeams_${uid}`) || '[]');
    const updated  = allTeams.map(t => {
      if (String(t.id) === String(teamInfo.id)) {
        return {
          ...t, ...teamInfo,
          chatHistory: chatMessagesList,
          members: members.map(m => ({
            ...m,
            role: m.role.replace('/팀장', ''),  // 중복 방지
            isLeader: m.isLeader,
            task: m.currentTask || '대기 중',
          })),
        };
      }
      return t;
    });
    localStorage.setItem(`allTeams_${uid}`, JSON.stringify(updated));
  }, [teamInfo, members, chatMessagesList]);

  useEffect(() => {
    const updated = members.map(m => {
      const totalTasks   = (m.currentTask ? 1 : 0) + m.todo.length + m.completed.length;
      const calcProgress = totalTasks === 0 ? 0 : Math.round((m.completed.length / totalTasks) * 100);
      return { ...m, contribution: calcProgress };
    });
    const totalAvg = updated.length === 0 ? 0 : Math.round(updated.reduce((acc, cur) => acc + cur.contribution, 0) / updated.length);
    if (JSON.stringify(updated) !== JSON.stringify(members)) setMembers(updated);
    if (teamInfo.progress !== totalAvg) setTeamInfo(prev => ({ ...prev, progress: totalAvg }));
  }, [members, teamInfo.progress]);

  const showCustomAlert   = (title) => setCustomModal({ show: true, title, onConfirm: () => setCustomModal(prev => ({ ...prev, show: false })), isConfirm: false });
  const showCustomConfirm = (title, onConfirm) => setCustomModal({ show: true, title, onConfirm, isConfirm: true });
  const addLog = (msg) => {
    setLogs(prev => {
      const updated = [msg, ...prev].slice(0, 20);
      localStorage.setItem(`teamLogs_${teamInfo.id}`, JSON.stringify(updated));

      // ✅ 백엔드에도 저장
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      fetch(`${apiUrl}/api/teams/${teamInfo.id}/info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activityLogs: JSON.stringify(updated) })
      }).catch(() => {});

      return updated;
    });
  };

  const getStatusColor = (status) => {
    if (status === '준비')    return '#64748b';
    if (status === '기획중')  return '#f59e0b';
    if (status === '개발중')  return BLUE;
    if (status === '개발완료') return '#22c55e';
    return '#64748b';
  };

  const getGaugeColor = (prog) => {
    if (prog <= 30) return '#ef4444';
    if (prog <= 70) return '#f59e0b';
    return BLUE;
  };

  const getDDay = () => {
    const dday = Math.ceil((new Date(teamInfo.deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return dday === 0 ? '(D-Day)' : dday > 0 ? `(D-${dday})` : `(D+${Math.abs(dday)})`;
  };

  const handleCopyInviteLink = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      let inviteCode = teamInfo.inviteCode;
      try {
        const res = await fetch(`${apiUrl}/api/teams/${teamInfo.id}/invite`, { method: 'POST' });
        if (res.ok) { const data = await res.json(); inviteCode = data.inviteCode; }
      } catch (e) {}
      if (!inviteCode) inviteCode = `${teamInfo.id.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const uid      = getUserId();
      const allTeams = JSON.parse(localStorage.getItem(`allTeams_${uid}`) || '[]');
      localStorage.setItem(`allTeams_${uid}`, JSON.stringify(allTeams.map(t => String(t.id) === String(teamInfo.id) ? { ...t, inviteCode } : t)));
      setTeamInfo(prev => ({ ...prev, inviteCode }));
      const inviteUrl = `${window.location.origin}/join?code=${inviteCode}`;
      navigator.clipboard.writeText(inviteUrl).then(() => { showCustomAlert('✅ 초대 링크가 복사되었습니다!\n\n같은 학교 팀원에게 공유하세요. 🔗'); addLog('초대 링크를 복사했습니다.'); });
    } catch (e) { showCustomAlert('초대 코드 생성 실패! 서버를 확인해주세요.'); }
  };

  const handleSendMessage = () => {
    if (!chatInput.trim()) return;
    const now = new Date();
    const timeStr = now.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: true });
    const newMsg = { id: Date.now(), sender: userName, senderId: String(userId), text: chatInput, isMine: true, time: timeStr };
    const updatedChat = [...chatMessagesList, newMsg];
    setChatMessagesList(updatedChat);
    setChatInput('');
    setTimeout(() => { if (chatMessagesRef.current) chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight; }, 50);

    // 백엔드에서 최신 chatHistory 불러온 후 merge해서 저장
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    fetch(`${apiUrl}/api/teams/${userId}`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            const cur = data.find(t => String(t.id) === String(teamInfo.id));
            let latestChat = [];
            if (cur?.chatHistory) {
              try { latestChat = JSON.parse(cur.chatHistory); } catch(e) {}
            }
            const exists = latestChat.some(m => m.id === newMsg.id);
            const mergedChat = exists ? latestChat : [...latestChat, newMsg];
            fetch(`${apiUrl}/api/teams/${teamInfo.id}/info`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ chatHistory: JSON.stringify(mergedChat) })
            }).then(() => {
              setChatMessagesList(mergedChat.map(msg => ({
                ...msg,
                isMine: String(msg.senderId) === String(userId)
              })));
            }).catch(() => {});
          }
        }).catch(() => {});
  };

  const deleteMember = (id) => {
    const target = members.find(m => m.id === id);
    showCustomConfirm('정말 이 팀원을 삭제하시겠습니까?', () => {
      const updatedMembers = members.filter(m => m.id !== id);
      setMembers(updatedMembers);
      addLog(`${target.name} 팀원이 제외되었습니다.`);
      setCustomModal(prev => ({ ...prev, show: false }));

      // 백엔드에서도 삭제
      // 백엔드에서도 삭제
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      fetch(`${apiUrl}/api/teams/${teamInfo.id}/members/${id}`, {
        method: 'DELETE'
      }).catch(() => {});

      // localStorage 즉시 반영
      const uid = getUserId();
      const allTeams = JSON.parse(localStorage.getItem(`allTeams_${uid}`) || '[]');
      const updated = allTeams.map(t => {
        if (String(t.id) === String(teamInfo.id)) {
          return {
            ...t,
            members: updatedMembers.map(m => ({
              ...m,
              role: m.role.replace('/팀장', ''),
              isLeader: m.isLeader,
              task: m.currentTask || '대기 중',
            })),
          };
        }
        return t;
      });
      localStorage.setItem(`allTeams_${uid}`, JSON.stringify(updated));
    });
  };

  const addNewMember = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const newMember = { id: Date.now(), name: '새 팀원', role: '역할 미정', isLeader: false, currentTask: '', todo: [], completed: [], contribution: 0 };
    try {
      const res = await fetch(`${apiUrl}/api/teams/${teamInfo.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newMember.name, role: newMember.role }),
      });
      if (res.ok) {
        const saved = await res.json();
        newMember.id = saved.id || newMember.id;
      }
    } catch (e) {}

    setMembers(prev => [...prev, newMember]);
    addLog(`${newMember.name} 님이 프로젝트에 참여했습니다.`); // ✅ 수정
  };

  const triggerFileSelect = (idx) => {
    fileInputRef.current.onchange = (e) => {
      const fileName = e.target.files[0]?.name;
      if (fileName) {
        const newCompleted = [...editModal.member.completed];
        newCompleted[idx].file = fileName;
        setEditModal({ ...editModal, member: { ...editModal.member, completed: newCompleted } });
      }
    };
    fileInputRef.current.click();
  };

  const handleDoneRightClick = (e, index) => { e.preventDefault(); setDoneContextMenu({ show: true, x: e.pageX, y: e.pageY, index }); };
  const deleteDoneItem = () => {
    const newCompleted = editModal.member.completed.filter((_, i) => i !== doneContextMenu.index);
    setEditModal({ ...editModal, member: { ...editModal.member, completed: newCompleted } });
    setDoneContextMenu({ show: false, x: 0, y: 0, index: null });
  };

  return (
    <div style={{ ...S.root, overflowX: 'hidden' }} onClick={() => { setStatusMenu(prev => ({ ...prev, show: false })); setDoneContextMenu(prev => ({ ...prev, show: false })); }}>

      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%',
        padding: isMobile ? '16px 14px 60px' : '32px 32px 60px',
        boxSizing: 'border-box',
      }}>
        {/* ── 상단 버튼 + 타이틀 ── */}
        <div style={{
          ...S.topRow,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '8px' : '10px',
          marginBottom: isMobile ? '14px' : '0',
        }}>
          <button style={S.backBtn} onClick={() => navigate('/team-project')}>← 팀 목록으로 돌아가기</button>
          <h1 style={{ ...S.pageTitle, fontSize: isMobile ? '16px' : '20px' }}>👥 팀 상세관리</h1>
          {/* 모바일 전용: 채팅 토글 버튼 */}
          {isMobile && (
            <button
              style={{ ...S.darkBtn, padding: '8px 14px', fontSize: '12px', alignSelf: 'flex-end' }}
              onClick={() => setShowChat(v => !v)}
            >
              💬 {showChat ? '채팅 닫기' : '팀 채팅 열기'}
            </button>
          )}
        </div>

        <div style={{
          ...S.inner,
          padding: isMobile ? '16px 0 0' : '28px 32px',
        }}>
          {/* ── 모바일: 채팅창 토글 영역 ── */}
          {isMobile && showChat && (
            <div style={{ ...S.chatContainer, height: '360px', marginBottom: '16px', position: 'static' }}>
              <div style={S.chatHeader}>💬 팀 채팅</div>
              <div style={S.chatMessages} ref={chatMessagesRef}>
                {chatMessagesList.map(msg => (
                  <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.isMine ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                    {!msg.isMine && <div style={S.senderName}>{msg.sender}</div>}
                    <div style={{ ...S.msgBubble, backgroundColor: msg.isMine ? BLUE : '#fff', color: msg.isMine ? '#fff' : '#1e293b', borderRadius: msg.isMine ? '14px 4px 14px 14px' : '4px 14px 14px 14px', border: msg.isMine ? 'none' : '1px solid #e2e8f0' }}>
                      {msg.text}
                    </div>
                    {msg.time && (
                        <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px', textAlign: msg.isMine ? 'right' : 'left' }}>
                          {msg.time}
                        </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={S.chatInputArea}>
                <input
                  style={S.chatInput}
                  placeholder="메시지를 입력하세요... (최대 200자)"
                  value={chatInput}
                  onChange={(e) => { if (e.target.value.length <= 200) setChatInput(e.target.value); }}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  maxLength={200}
                />
                <button style={S.sendBtn} onClick={handleSendMessage}>전송</button>
              </div>
            </div>
          )}

          <div style={{
            ...S.wrapper,
            // 모바일: 1열, 채팅 숨김
            gridTemplateColumns: isMobile ? '1fr' : '1fr 360px',
            gap: isMobile ? '14px' : '24px',
          }}>

            {/* ── 왼쪽 패널 ── */}
            <div style={S.leftPanel}>

              {/* 헤더 카드 */}
              <section style={{
                ...S.headerCard,
                // 모바일: 세로 배치
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '20px',
              }}>
                {/* 활동 로그 */}
                <div style={{
                  ...S.logBox,
                  width: isMobile ? '100%' : '200px',
                  boxSizing: 'border-box',
                }}>
                  <div style={S.logTitle}>최신 활동 로그</div>
                  {logs.map((l, i) => <div key={i} style={S.logItem}>• {l}</div>)}
                </div>

                {/* 프로젝트 정보 */}
                <div style={{ flex: 1, marginLeft: isMobile ? '0' : '20px', minWidth: 0 }}>
                  <div style={S.projLabel}>TEAM PROJECT</div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ ...S.projTitle, fontSize: isMobile ? '16px' : '20px' }}>{teamInfo.projectTitle}</span>
                    <span style={S.projSub}> | {teamInfo.name}</span>
                  </div>
                  <div style={{ ...S.infoRow, flexWrap: 'wrap', gap: '8px' }}>
                    <span
                      style={{ ...S.statusBadge, backgroundColor: getStatusColor(teamInfo.status || '준비'), cursor: 'pointer' }}
                      onContextMenu={(e) => { e.preventDefault(); setStatusMenu({ show: true, x: e.pageX, y: e.pageY }); }}
                    >
                      {teamInfo.status || '준비'}
                    </span>
                    <span style={{ ...S.deadlineText, fontSize: isMobile ? '12px' : '13px' }}>
                      📅 마감:&nbsp;
                      <input type="date" value={teamInfo.deadline} onChange={(e) => setTeamInfo({ ...teamInfo, deadline: e.target.value })} style={{ ...S.dateInput, maxWidth: isMobile ? '130px' : 'auto' }} />
                      <span style={{ color: '#ef4444', marginLeft: '6px', fontWeight: '700' }}>{getDDay()}</span>
                    </span>
                  </div>
                </div>

                {/* 진행률 */}
                <div style={{
                  ...S.progressBox,
                  borderLeft: isMobile ? 'none' : '1px solid #f1f5f9',
                  borderTop: isMobile ? '1px solid #f1f5f9' : 'none',
                  paddingLeft: isMobile ? '0' : '20px',
                  paddingTop: isMobile ? '12px' : '0',
                  display: 'flex', flexDirection: isMobile ? 'row' : 'column',
                  alignItems: isMobile ? 'center' : 'center',
                  gap: isMobile ? '12px' : '0',
                }}>
                  <div>
                    <div style={S.progressLabel}>전체 진행률</div>
                    <div style={{ ...S.progressNum, fontSize: isMobile ? '28px' : '36px', color: getGaugeColor(teamInfo.progress) }}>{teamInfo.progress}%</div>
                  </div>
                  <button style={S.progressBtn} onClick={() => setProgressModal(true)}>상세 현황</button>
                </div>
              </section>

              {/* 공지 카드 */}
              <section style={S.noticeCard}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '0',
                }}>
                  <h3 style={S.noticeTitle}>🚨 팀 긴급 공지사항</h3>
                  {amILeader && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button style={S.noticeBtn} onClick={() => setNoticeModal({ show: true, mode: 'list' })}>공지목록</button>
                        <button style={{ ...S.noticeBtn, backgroundColor: BLUE, color: '#fff', borderColor: BLUE }} onClick={() => setNoticeModal({ show: true, mode: 'add' })}>공지추가</button>
                        <button style={{ ...S.noticeBtn, backgroundColor: '#ef4444', color: '#fff', borderColor: '#ef4444' }} onClick={() => setNoticeModal({ show: true, mode: 'edit' })}>수정</button>
                      </div>
                  )}
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>{teamInfo.noticeTitle || '게시된 공지 없음'}</div>
                  <p style={{ margin: 0, fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>{teamInfo.notice || '현재 등록된 팀 공지가 없습니다.'}</p>
                </div>
              </section>

              {/* 팀원 작업 현황판 */}
              <section>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '0',
                  marginBottom: '14px',
                }}>
                  <h3 style={{ ...S.sectionTitle, fontSize: isMobile ? '15px' : '18px' }}>👥 팀원 작업 현황판</h3>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button style={{ ...S.primaryBtn, flex: 'none', padding: '8px 12px', fontSize: '12px' }} onClick={handleCopyInviteLink}>🔗 초대 링크 복사</button>
                    {amILeader && <button style={{ ...S.darkBtn, padding: '8px 12px', fontSize: '12px' }} onClick={addNewMember}>+ 팀원 추가</button>}
                  </div>
                </div>

                {/* ✅ 스크롤 wrapper */}
                <div style={{
                  maxHeight: isMobile ? '520px' : '600px',
                  overflowY: 'auto',
                  paddingRight: '4px',
                }}>
                  <div style={{
                    ...S.memberGrid,
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    gap: isMobile ? '12px' : '16px',
                  }}>
                    {members.map((member, memberIdx) => (
                      <div key={member.id} style={S.memberCard}>
                        <div style={S.memberTop}>
                          <div style={{ position: 'relative' }}>
                            {member.isLeader && <span style={{ fontSize: '16px', position: 'absolute', top: '-20px', left: 0 }}>👑</span>}
                            <span style={S.memberName}>{member.name}</span>
                            <span style={S.memberRole}>{member.role}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {(amILeader || member.name === userName) &&
                                <button style={S.editBtn} onClick={() => setEditModal({ show: true, member: JSON.parse(JSON.stringify(member)) })}>편집</button>
                            }
                            {amILeader && memberIdx !== 0 &&
                                <button style={S.delBtn} onClick={() => deleteMember(member.id)}>삭제</button>
                            }
                          </div>
                        </div>
                        <div style={S.taskLine}><strong style={{ ...S.taskLabel, color: '#ef4444' }}>진행중:</strong><span>{member.currentTask || '대기 중'}</span></div>
                        <div style={S.taskLine}><strong style={{ ...S.taskLabel, color: BLUE }}>할 일:</strong><span>{member.todo.join(', ') || '없음'}</span></div>
                        <div style={{ ...S.taskLine, alignItems: 'flex-start' }}>
                          <strong style={{ ...S.taskLabel, color: '#22c55e' }}>완료:</strong>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {member.completed.length > 0
                              ? member.completed.map((c, i) => (
                                  <span key={i} style={S.doneTag} onClick={() => showCustomAlert(c.file ? `${c.file} 파일을 확인합니다.` : '첨부된 파일이 없습니다.')}>
                                    {c.title} {c.file && '📎'}
                                  </span>
                                ))
                              : <span style={{ color: '#94a3b8', fontSize: '12px' }}>완료된 작업 없음</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </div>

            {/* ── 채팅 패널 (데스크탑만) ── */}
            {!isMobile && (
              <div style={S.rightPanel}>
                <div style={S.chatContainer}>
                  <div style={S.chatHeader}>💬 팀 채팅</div>
                  <div style={S.chatMessages} ref={chatMessagesRef}>
                    {chatMessagesList.map(msg => (
                      <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.isMine ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                        {!msg.isMine && <div style={S.senderName}>{msg.sender}</div>}
                        <div style={{ ...S.msgBubble, backgroundColor: msg.isMine ? BLUE : '#fff', color: msg.isMine ? '#fff' : '#1e293b', borderRadius: msg.isMine ? '14px 4px 14px 14px' : '4px 14px 14px 14px', border: msg.isMine ? 'none' : '1px solid #e2e8f0' }}>
                          {msg.text}
                        </div>
                        {msg.time && (
                            <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px', textAlign: msg.isMine ? 'right' : 'left' }}>
                              {msg.time}
                            </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={S.chatInputArea}>
                    <input
                      style={S.chatInput}
                      placeholder="메시지를 입력하세요..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <button style={S.sendBtn} onClick={handleSendMessage}>전송</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── 커스텀 모달 ── */}
      {customModal.show && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <p style={S.modalMsg}>{customModal.title}</p>
            <div style={S.modalBtnRow}>
              {customModal.isConfirm && <button style={S.secondaryBtn} onClick={() => setCustomModal(prev => ({ ...prev, show: false }))}>취소</button>}
              <button style={S.primaryBtn} onClick={customModal.onConfirm}>확인</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 팀원 편집 모달 ── */}
      {editModal.show && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, maxWidth: '520px', textAlign: 'left' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', textAlign: 'center' }}>팀원 상세 관리</h2>
            {editModal.member.isLeader && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', padding: '8px 12px', backgroundColor: BLUE_BG, borderRadius: '8px' }}>
                  <span style={{ fontSize: '14px' }}>👑</span>
                  <span style={{ fontWeight: '700', fontSize: '13px', color: BLUE }}>팀장</span>
                </div>
            )}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '120px' }}>
                <label style={S.modalLabel}>이름</label>
                <input style={S.modalInput} value={editModal.member.name} onChange={e => { if (e.target.value.length <= 10) setEditModal({ ...editModal, member: { ...editModal.member, name: e.target.value } }); }} maxLength={10} />
              </div>
              <div style={{ flex: 1, minWidth: '120px' }}>
                <label style={S.modalLabel}>역할</label>
                <input style={S.modalInput} value={editModal.member.role} onChange={e => { if (e.target.value.length <= 20) setEditModal({ ...editModal, member: { ...editModal.member, role: e.target.value } }); }} maxLength={20} />
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={S.modalLabel}>할 일 목록 추가</label>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                <input style={{ ...S.modalInput, marginBottom: 0, flex: 1, minWidth: 0 }} id="todoNew" placeholder="엔터 또는 + 클릭 (최대 50자)" maxLength={50} onKeyDown={(e) => { if (e.key === 'Enter') document.getElementById('addTodoBtn').click(); }} />
                <button id="addTodoBtn" style={{ ...S.primaryBtn, flex: 'none', padding: '0 14px' }} onClick={() => { const val = document.getElementById('todoNew').value; if (val) { setEditModal({ ...editModal, member: { ...editModal.member, todo: [...editModal.member.todo, val] } }); document.getElementById('todoNew').value = ''; } }}>+</button>
              </div>
              {editModal.member.todo.map((t, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#f8fafc', borderRadius: '8px', marginBottom: '4px', fontSize: '12px', fontWeight: '600' }}>
                  {t}
                  <button style={{ padding: '3px 8px', fontSize: '11px', background: BLUE, color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }} onClick={() => { const newTodo = editModal.member.todo.filter((_, i) => i !== idx); setEditModal({ ...editModal, member: { ...editModal.member, todo: newTodo, currentTask: t } }); }}>진행 시작</button>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={S.modalLabel}>현재 진행 중인 작업</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input style={{ ...S.modalInput, marginBottom: 0, flex: 1, minWidth: 0 }} value={editModal.member.currentTask} readOnly placeholder="진행 시작 버튼을 누르세요" />
                <button style={{ ...S.primaryBtn, flex: 'none', padding: '0 12px', backgroundColor: '#22c55e' }} onClick={() => { if (!editModal.member.currentTask) return; const newTask = { title: editModal.member.currentTask, file: null }; addLog(`${editModal.member.name} 님이 '${editModal.member.currentTask}' 완료!`); setEditModal({ ...editModal, member: { ...editModal.member, completed: [...editModal.member.completed, newTask], currentTask: '' } }); }}>완료 확정</button>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={S.modalLabel}>완료 기록 (클릭하여 파일 첨부 / 우클릭하여 삭제)</label>
              <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '6px', maxHeight: '130px', overflowY: 'auto' }}>
                {editModal.member.completed.map((task, idx) => (
                  <div key={idx} style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }} onClick={() => triggerFileSelect(idx)} onContextMenu={(e) => handleDoneRightClick(e, idx)}>
                    <span>{task.title}</span>
                    {task.file ? <span style={{ color: BLUE }}>📎 {task.file}</span> : <span style={{ color: '#94a3b8' }}>파일 없음</span>}
                  </div>
                ))}
              </div>
            </div>
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} />
            <div style={S.modalBtnRow}>
              <button style={{ ...S.primaryBtn, flex: 2, backgroundColor: '#1e293b' }} onClick={() => {
                const updatedMembers = members.map(m => m.id === editModal.member.id ? editModal.member : m);
                setMembers(updatedMembers);
                setEditModal({ show: false, member: null });

                // ✅ localStorage에 즉시 저장
                const uid = getUserId();
                const allTeams = JSON.parse(localStorage.getItem(`allTeams_${uid}`) || '[]');
                const updated = allTeams.map(t => {
                  if (String(t.id) === String(teamInfo.id)) {
                    return {
                      ...t,
                      members: updatedMembers.map(m => ({
                        ...m,
                        role: m.role.replace('/팀장', ''),
                        isLeader: m.isLeader,
                        task: m.currentTask || '대기 중',
                      })),
                    };
                  }
                  return t;
                });
                localStorage.setItem(`allTeams_${uid}`, JSON.stringify(updated));
              }}>변경사항 저장</button>
              <button style={S.secondaryBtn} onClick={() => setEditModal({ show: false })}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 진행 현황 모달 ── */}
      {progressModal && (
        <div style={S.modalOverlay} onClick={() => setProgressModal(false)}>
          <div style={{ ...S.modal, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', textAlign: 'center' }}>📊 팀원별 진행 현황</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {members.map(m => (
                <div key={m.id} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '700', fontSize: '15px' }}>{m.isLeader && '👑 '}{m.name}</span>
                    <span style={{ fontWeight: '700', fontSize: '15px', color: getGaugeColor(m.contribution) }}>{m.contribution}%</span>
                  </div>
                  <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${m.contribution}%`, backgroundColor: getGaugeColor(m.contribution), borderRadius: '99px', transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>✅ 완료된 작업 리스트</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {m.completed.length > 0
                        ? m.completed.map((c, i) => <div key={i} style={{ padding: '4px 10px', background: '#f1f5f9', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: '1px solid #e2e8f0' }}>{c.title}</div>)
                        : <div style={{ color: '#94a3b8', fontSize: '12px' }}>아직 완료된 작업이 없습니다.</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button style={{ ...S.darkBtn, width: '100%', marginTop: '20px' }} onClick={() => setProgressModal(false)}>확인 완료</button>
          </div>
        </div>
      )}

      {/* ── 공지 모달 ── */}
      {noticeModal.show && (
        <div style={S.modalOverlay} onClick={() => setNoticeModal({ show: false })}>
          <div style={{ ...S.modal, maxWidth: '560px', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', textAlign: 'center' }}>
              {noticeModal.mode === 'list' ? '📜 공지목록' : noticeModal.mode === 'edit' ? '✍️ 공지수정' : noticeModal.mode === 'detail' ? '📄 공지상세' : '📄 공지추가'}
            </h2>
            {noticeModal.mode === 'list' && (
              <div>
                <div style={{ maxHeight: '240px', overflowY: 'auto', marginBottom: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '8px' }}>
                  {teamInfo.noticeHistory.map(h => (
                    <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setNoticeModal({ ...noticeModal, mode: 'detail', selectedNotice: h })}>
                        <div style={{ fontSize: '10px', color: '#94a3b8' }}>{h.date}</div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{h.title}</div>
                      </div>
                      {amILeader && (
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button style={{ ...S.noticeBtn, fontSize: '11px' }} onClick={(e) => { e.stopPropagation(); setTeamInfo({ ...teamInfo, noticeTitle: h.title, notice: h.text }); addLog('공지가 등록되었습니다.'); setNoticeModal({ show: false }); }}>등록</button>
                            <button style={{ ...S.noticeBtn, color: '#ef4444', borderColor: '#fca5a5', fontSize: '11px' }} onClick={(e) => { e.stopPropagation(); const isCur = teamInfo.noticeTitle === h.title; setTeamInfo({ ...teamInfo, noticeHistory: teamInfo.noticeHistory.filter(n => n.id !== h.id), noticeTitle: isCur ? '' : teamInfo.noticeTitle, notice: isCur ? '' : teamInfo.notice }); addLog('공지가 삭제되었습니다.'); }}>삭제</button>
                          </div>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px dashed #e2e8f0' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '8px', color: '#1e293b' }}>🆕 새 공지 등록</div>
                  <input id="listNewNoticeTitle" style={S.modalInput} placeholder="공지 제목 (최대 30자)" maxLength={30} />
                  <textarea id="listNewNoticeText" style={{ ...S.modalInput, height: '70px', resize: 'vertical' }} placeholder="공지 내용 (최대 200자)" maxLength={200} />
                  <button style={{ ...S.primaryBtn, width: '100%' }} onClick={() => { const nt = document.getElementById('listNewNoticeTitle').value; const nc = document.getElementById('listNewNoticeText').value; if (!nt || !nc) return showCustomAlert('내용을 입력하세요'); setTeamInfo({ ...teamInfo, noticeTitle: nt, notice: nc, noticeHistory: [{ id: Date.now(), title: nt, text: nc, date: new Date().toLocaleDateString() }, ...teamInfo.noticeHistory] }); addLog('새 공지가 등록되었습니다.'); setNoticeModal({ show: false }); }}>공지 등록하기</button>
                </div>
              </div>
            )}
            {noticeModal.mode === 'detail' && (
              <div>
                <button style={S.secondaryBtn} onClick={() => setNoticeModal({ ...noticeModal, mode: 'list' })}>← 목록으로</button>
                <h3 style={{ marginTop: '14px', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{noticeModal.selectedNotice.title}</h3>
                <p style={{ padding: '14px', background: '#f8fafc', borderRadius: '10px', minHeight: '80px', fontSize: '13px', lineHeight: '1.6', border: '1px solid #e2e8f0', marginTop: '8px', color: '#374151' }}>{noticeModal.selectedNotice.text}</p>
              </div>
            )}
            {(noticeModal.mode === 'add' || noticeModal.mode === 'edit') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <input id="editNoticeTitle" style={S.modalInput} placeholder="공지 제목 (최대 30자)" maxLength={30} defaultValue={noticeModal.mode === 'edit' ? teamInfo.noticeTitle : ''} />
                <textarea id="editNoticeText" style={{ ...S.modalInput, height: '70px', resize: 'vertical' }} placeholder="공지 내용 (최대 200자)" maxLength={200} defaultValue={noticeModal.mode === 'edit' ? teamInfo.notice : ''} />
                <button style={{ ...S.primaryBtn, backgroundColor: noticeModal.mode === 'edit' ? '#ef4444' : BLUE }} onClick={() => { const nt = document.getElementById('editNoticeTitle').value; const nc = document.getElementById('editNoticeText').value; if (!nt || !nc) return showCustomAlert('내용을 입력하세요'); setTeamInfo({ ...teamInfo, noticeTitle: nt, notice: nc, noticeHistory: noticeModal.mode === 'add' ? [{ id: Date.now(), title: nt, text: nc, date: new Date().toLocaleDateString() }, ...teamInfo.noticeHistory] : teamInfo.noticeHistory }); addLog(noticeModal.mode === 'add' ? '새 공지가 등록되었습니다.' : '공지가 수정되었습니다.'); setNoticeModal({ show: false }); }}>{noticeModal.mode === 'edit' ? '수정사항 저장' : '공지 등록하기'}</button>
              </div>
            )}
            <button style={{ ...S.secondaryBtn, width: '100%', marginTop: '12px' }} onClick={() => setNoticeModal({ show: false })}>닫기</button>
          </div>
        </div>
      )}

      {/* ── 상태 컨텍스트 메뉴 ── */}
      {statusMenu.show && (
        <div style={{ ...S.contextMenu, top: statusMenu.y, left: statusMenu.x }}>
          {['준비','기획중','개발중','개발완료'].map(s => (
            <div key={s} style={{ ...S.contextItem, color: getStatusColor(s) }} onClick={() => { setTeamInfo({ ...teamInfo, status: s }); addLog(`상태가 [${s}]로 변경됨`); setStatusMenu(prev => ({ ...prev, show: false })); }}>{s}</div>
          ))}
        </div>
      )}

      {/* ── 완료 기록 컨텍스트 메뉴 ── */}
      {doneContextMenu.show && (
        <div style={{ ...S.contextMenu, top: doneContextMenu.y, left: doneContextMenu.x }}>
          <div style={{ ...S.contextItem, color: '#ef4444' }} onClick={deleteDoneItem}>삭제하기</div>
        </div>
      )}
    </div>
  );
}

// ── 스타일 (원본 그대로) ─────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh', backgroundColor: '#f8fafc',
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif",
    color: '#1e293b',
  },
  topRow:    { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', maxWidth: '1400px', marginBottom: '0' },
  backBtn:   { padding: '9px 14px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  pageTitle: { fontSize: '20px', fontWeight: '800', color: '#1e293b', margin: 0 },
  inner:     { maxWidth: '1100px', margin: '0 auto', padding: '28px 32px', boxSizing: 'border-box', width: '100%' },
  wrapper:   { display: 'grid', gridTemplateColumns: '1fr 360px', gap: '24px' },
  leftPanel: { display: 'flex', flexDirection: 'column', gap: '20px' },
  headerCard: {
    backgroundColor: '#fff', borderRadius: '14px', padding: '20px 22px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9',
    display: 'flex', gap: '20px', alignItems: 'stretch', boxSizing: 'border-box',
  },
  logBox:       { width: '200px', backgroundColor: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0', flexShrink: 0 },
  logTitle:     { fontSize: '12px', fontWeight: '700', color: BLUE, marginBottom: '8px' },
  logItem:      { fontSize: '11px', fontWeight: '600', color: '#ef4444', marginBottom: '4px' },
  projLabel:    { fontSize: '11px', color: BLUE, fontWeight: '700', marginBottom: '4px' },
  projTitle:    { fontSize: '20px', fontWeight: '800', color: '#1e293b' },
  projSub:      { fontSize: '14px', color: BLUE, fontWeight: '700' },
  infoRow:      { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' },
  statusBadge:  { padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: '#fff' },
  deadlineText: { fontSize: '13px', fontWeight: '600', color: '#374151' },
  dateInput:    { border: '1px solid #e2e8f0', padding: '4px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#374151' },
  progressBox:  { textAlign: 'center', borderLeft: '1px solid #f1f5f9', paddingLeft: '20px', flexShrink: 0 },
  progressLabel:{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' },
  progressNum:  { fontSize: '36px', fontWeight: '800' },
  progressBtn:  { marginTop: '8px', padding: '7px 14px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' },
  noticeCard:   { backgroundColor: '#fff', borderRadius: '14px', padding: '18px 22px', border: '1px solid #fecaca', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', boxSizing: 'border-box' },
  noticeTitle:  { margin: 0, fontSize: '14px', fontWeight: '700', color: '#ef4444' },
  noticeBtn:    { padding: '5px 10px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '7px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', color: '#374151' },
  sectionTitle: { fontSize: '18px', fontWeight: '800', color: '#1e293b' },
  memberGrid:   { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' },
  memberCard:   { backgroundColor: '#fff', borderRadius: '14px', padding: '18px 20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', boxSizing: 'border-box' },
  memberTop:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' },
  memberName:   { fontSize: '16px', fontWeight: '800', color: '#1e293b' },
  memberRole:   { display: 'inline-block', padding: '3px 8px', backgroundColor: BLUE_BG, color: BLUE, borderRadius: '6px', fontSize: '11px', fontWeight: '700', marginLeft: '6px' },
  editBtn:      { padding: '5px 10px', backgroundColor: '#f1f5f9', border: 'none', borderRadius: '7px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', color: '#374151' },
  delBtn:       { padding: '5px 10px', backgroundColor: '#fee2e2', border: 'none', borderRadius: '7px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', color: '#b91c1c' },
  taskLine:     { fontSize: '12px', lineHeight: '1.6', marginBottom: '6px', display: 'flex' },
  taskLabel:    { fontWeight: '700', width: '56px', flexShrink: 0 },
  doneTag:      { display: 'inline-block', padding: '3px 8px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', color: '#374151' },
  rightPanel:   { position: 'sticky', top: '20px', height: 'calc(100vh - 300px)' },
  chatContainer:{ height: '100%', backgroundColor: '#fff', borderRadius: '14px', display: 'flex', flexDirection: 'column', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)', overflow: 'hidden' },
  chatHeader:   { padding: '16px 18px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  chatMessages: { flex: 1, padding: '14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', backgroundColor: '#f8fafc' },
  msgBubble:    { padding: '9px 13px', fontSize: '12px', fontWeight: '500', maxWidth: '85%' },
  senderName:   { fontSize: '10px', fontWeight: '700', color: '#64748b', marginBottom: '3px', marginLeft: '4px' },
  chatInputArea:{ padding: '12px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '8px' },
  chatInput:    { flex: 1, border: '1px solid #e2e8f0', padding: '10px 12px', borderRadius: '10px', fontSize: '12px', color: '#374151', outline: 'none', backgroundColor: '#f8fafc', fontFamily: 'inherit', minWidth: 0 },
  sendBtn:      { padding: '0 16px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '12px', cursor: 'pointer' },
  primaryBtn:   { flex: 1, padding: '11px 16px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  secondaryBtn: { flex: 1, padding: '11px 16px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  darkBtn:      { padding: '10px 18px', backgroundColor: '#1e293b', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 3000 },
  modal:        { backgroundColor: '#fff', padding: '28px 32px', borderRadius: '14px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', width: '90%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' },
  modalMsg:     { fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: '0 0 20px', lineHeight: '1.5', textAlign: 'center', whiteSpace: 'pre-line' },
  modalBtnRow:  { display: 'flex', gap: '10px', marginTop: '16px' },
  modalLabel:   { fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px', display: 'block' },
  modalInput:   { width: '100%', padding: '10px 12px', marginBottom: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#374151', boxSizing: 'border-box', outline: 'none', backgroundColor: '#f8fafc', fontFamily: 'inherit' },
  contextMenu:  { position: 'absolute', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 8px 20px rgba(0,0,0,0.12)', zIndex: 4000, padding: '6px 0', minWidth: '120px' },
  contextItem:  { padding: '10px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' },
};

export default TeamManagementPage;